import { BOOTH_STATUS } from '../../../../utils/boothMap';

/**
 * Display helpers for the booth status vocabulary of the hall-plan booking page.
 * Every label is a literal t() call so the keys stay extractable for Locize.
 */

/**
 * Short status label for chips, search hits and the aria-label of a booth. A free booth says
 * "book now" only while booth booking is open (`bookingOpen`, default true).
 */
export function getStatusLabel(t, status, bookingOpen = true) {
  switch (status) {
    case BOOTH_STATUS.FREE:
      return bookingOpen === false ? t('Available') : t('Available · book now');
    case BOOTH_STATUS.RESERVED_FOR_YOU:
      return t('Reserved for you');
    case BOOTH_STATUS.HELD_BY_YOU:
      return t('Held for you');
    case BOOTH_STATUS.HELD_BY_OTHER:
    case BOOTH_STATUS.RESERVED_FOR_OTHER:
      return t('Reserved · option running');
    case BOOTH_STATUS.SOLD:
      return t('Sold');
    default:
      return t('Not bookable');
  }
}

/** Modifier of .booth-status-chip / .booth-area / .booth-legend-swatch for a status. */
export function statusVariant(status) {
  switch (status) {
    case BOOTH_STATUS.FREE:
      return 'free';
    case BOOTH_STATUS.HELD_BY_YOU:
    case BOOTH_STATUS.RESERVED_FOR_YOU:
      return 'mine';
    case BOOTH_STATUS.HELD_BY_OTHER:
    case BOOTH_STATUS.RESERVED_FOR_OTHER:
      return 'reserved';
    case BOOTH_STATUS.SOLD:
      return 'sold';
    default:
      return 'unavailable';
  }
}

/** Area in m² for display ('–' when unknown). */
export function formatArea(area) {
  const value = Number(area);
  if (area === null || area === undefined || area === '' || !Number.isFinite(value)) return '–';
  return value.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

/** Length in m for display ('–' when unknown). */
export function formatLength(length) {
  return formatArea(length);
}

/**
 * Label of a position type key (data-position): the four standard keys are translated, free keys
 * of the plan (e.g. "wall") are shown as they come. Case-insensitive on the trimmed key.
 */
export function positionTypeLabel(t, key) {
  const raw = key === null || key === undefined ? '' : String(key).trim();
  switch (raw.toLowerCase()) {
    case 'row':
      return t('Row stand');
    case 'corner':
      return t('Corner stand');
    case 'head':
      return t('Head stand');
    case 'island':
      return t('Island stand');
    default:
      return raw;
  }
}

/** Label of a zone key (data-zone), e.g. "Zone V". */
export function zoneLabel(t, zone) {
  const raw = zone === null || zone === undefined ? '' : String(zone).trim();
  return raw ? t('Zone {{zone}}', { zone: raw }) : '';
}

/** Label of one surcharge of a price breakdown: "Corner stand surcharge 17 %", "Zone V surcharge", "Corner stand rate 309,00 €/m²". */
function surchargeLabel(t, surcharge, formatMoney) {
  const subject = surcharge.on === 'zone' ? zoneLabel(t, surcharge.key) : positionTypeLabel(t, surcharge.key);
  const value = Number(surcharge.value);
  if (surcharge.type === 'rate') {
    return Number.isFinite(value)
      ? t('{{subject}} rate {{rate}}/m²', { subject, rate: formatMoney(value) })
      : t('{{subject}} rate', { subject });
  }
  if (surcharge.type === 'percent' && Number.isFinite(value)) {
    return t('{{subject}} surcharge {{percent}} %', { subject, percent: value.toLocaleString('de-DE', { maximumFractionDigits: 2 }) });
  }
  return t('{{subject}} surcharge', { subject });
}

/** "DD.MM.YYYY" of an ISO date-time / AWSDate in Europe/Berlin, '' when unparsable. */
function formatTierDate(value) {
  if (typeof value !== 'string' || !value.trim()) return '';
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (day) return `${day[3]}.${day[2]}.${day[1]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
}

/**
 * Text lines of a price breakdown (priceBreakdownOf result) for the details panel and the cart:
 * one "incl. {{label}} {{amount}}" line per surcharge, then the time tier ("Price valid until …"),
 * the minimum price and, for a price per m², "{{sqm}} m² × {{rate}}". [] for null.
 *
 * @param {function} t i18next t
 * @param {object|null} breakdown priceBreakdownOf(booth.priceDetails)
 * @param {function} formatMoney amount -> text (transformMoney)
 */
export function priceBreakdownLines(t, breakdown, formatMoney) {
  if (!breakdown) return [];
  const lines = [];
  if (breakdown.mode === 'perSqm' && breakdown.sqm && breakdown.rate !== null && breakdown.rate !== undefined) {
    lines.push(t('{{sqm}} m² × {{rate}}', { sqm: formatArea(breakdown.sqm), rate: formatMoney(breakdown.rate) }));
  }
  for (const surcharge of breakdown.surcharges || []) {
    const amount = Number(surcharge.amount);
    lines.push(
      t('incl. {{label}} {{amount}}', {
        label: surchargeLabel(t, surcharge, formatMoney),
        amount: Number.isFinite(amount) ? formatMoney(amount) : '',
      }).trim()
    );
  }
  if (breakdown.tier) {
    const until = formatTierDate(breakdown.tier.end);
    const from = formatTierDate(breakdown.tier.start);
    if (until) lines.push(t('Price valid until {{date}}', { date: until }));
    else if (from) lines.push(t('Price valid from {{date}}', { date: from }));
  }
  if (breakdown.minPriceApplied) lines.push(t('Minimum price applied'));
  return lines;
}

/** AWSDate day "YYYY-MM-DD" as "DD.MM.YYYY" (no Date parsing, so no time-zone shift), '' otherwise. */
export function formatDay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof value === 'string' ? value : '');
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '';
}

/**
 * Short reason next to a button that is disabled because booth booking is closed
 * (boothBookingWindow result): "Booking opens on DD.MM.YYYY", "Booking closed" after the window,
 * otherwise (switch off / event not active / no window info) "Booth booking is not open for this event".
 */
export function bookingClosedReason(t, bookingWindow) {
  const reason = bookingWindow && bookingWindow.reason;
  const date = formatDay(bookingWindow && bookingWindow.date);
  if (reason === 'not-started' && date) return t('Booking opens on {{date}}', { date });
  if (reason === 'ended') return t('Booking closed');
  return t('Booth booking is not open for this event');
}

/** Whether a booth is held by / reserved for the caller's company. */
export function isOwnStatus(status) {
  return status === BOOTH_STATUS.HELD_BY_YOU || status === BOOTH_STATUS.RESERVED_FOR_YOU;
}
